import { Link } from "react-router-dom";
import {
  Shield, Palette, Radio, Headphones, BarChart3,
  Music, Tv, MessageCircle, Zap, Crown, Check, X, ArrowRight
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const features = [
  { icon: Palette, title: "Full Branding Control", desc: "Your logo, colors, domain, and custom CSS. Your station, your identity — zero platform branding forced on you." },
  { icon: Tv, title: "Live Video Streaming", desc: "Stream video alongside your audio. No other indie radio platform gives you an integrated video player." },
  { icon: Radio, title: "AutoDJ & Playlist Scheduling", desc: "Upload your music library, build playlists, and schedule them weekly. Your station runs 24/7 even when you're offline." },
  { icon: Music, title: "Song Requests & Dedications", desc: "Listeners request songs and send dedications directly from your player. Real-time engagement, built in." },
  { icon: MessageCircle, title: "Live Chat", desc: "Built-in live chat on your player. Connect with your audience in real time — no third-party widgets needed." },
  { icon: BarChart3, title: "Real-Time Analytics", desc: "Track listeners, peak hours, and song performance from your dashboard. Know your audience." },
  { icon: Headphones, title: "Premium Audio Visualizer", desc: "Canvas-based FFT visualizer reacts to your stream in real time. Looks incredible, works automatically." },
  { icon: Zap, title: "Liquidsoap Editor", desc: "Advanced users can inject custom crossfades, jingles, and sweepers with our built-in code editor and preset templates." },
];

const comparison = [
  { feature: "White-Label Branded Player", us: true, them: false },
  { feature: "Custom Logo & Colors", us: true, them: false },
  { feature: "Live Video Streaming", us: true, them: false },
  { feature: "Built-in Live Chat", us: true, them: false },
  { feature: "Song Requests from Listeners", us: true, them: false },
  { feature: "AutoDJ & Playlist Scheduling", us: true, them: "Limited" },
  { feature: "Audio Visualizer", us: true, them: false },
  { feature: "Real-Time Analytics Dashboard", us: true, them: "Limited" },
  { feature: "Liquidsoap Custom Code", us: true, them: false },
  { feature: "News Ticker", us: true, them: false },
  { feature: "Dedicated Support", us: true, them: "Paid" },
  { feature: "Affordable Indie Pricing", us: true, them: false },
];

export default function WhyUs() {
  return (
    <div className="min-h-screen bg-[#0a0a14] text-white">
      <Header />

      <main>
        <SEO
          title="Why Choose Us"
          description="See why DJsandMCMedia™ is the best platform for independent DJs, MCs, and Podcasters. Full branding control, live video, AutoDJ, song requests, and more — features no other platform offers."
        />
        {/* Hero with Background Video */}
        <section className="relative overflow-hidden" style={{ minHeight: "520px" }}>
          {/* Background Video */}
          <video
            autoPlay muted loop playsInline
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="whyus-hero-video"
          >
            <source src="/whyus-hero.mp4" type="video/mp4" />
          </video>
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,20,0.2) 0%, rgba(10,10,20,0.9) 100%)" }} />

          {/* Content */}
          <div className="relative z-10 max-w-5xl mx-auto px-6 pt-28 pb-20 text-center">
            <div className="inline-flex items-center gap-2 bg-purple-500/15 border border-purple-500/25 px-4 py-1.5 rounded-full text-purple-300 text-sm mb-6 backdrop-blur-sm">
              <Crown className="w-4 h-4" /> Built for Creators
            </div>
            <h1 data-testid="whyus-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight drop-shadow-lg">
              Why DJsandMCMedia<span className="text-purple-400">&trade;</span>?
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto mb-8 drop-shadow">
              Other platforms are built for listeners. We're built for <span className="text-white font-semibold">you</span> — the DJ, the MC, the podcaster. 
              Full control over your channel, your brand, and your audience.
            </p>
            <Link
              to="/#pricing"
              data-testid="whyus-hero-cta"
              className="inline-flex items-center gap-2 text-white px-8 py-3.5 rounded-full font-semibold transition hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 text-sm"
              style={{ background: "linear-gradient(135deg, #8B5CF6, #ec4899)" }}
            >
              Start Your Channel <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Key Differentiators */}
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">What Sets Us Apart</h2>
          <p className="text-white/40 text-sm text-center mb-12 max-w-lg mx-auto">Features you won't find on traditional streaming platforms.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="whyus-features-grid">
            {features.map((f, i) => (
              <div
                key={i}
                data-testid={`whyus-feature-${i}`}
                className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 hover:border-purple-500/20 hover:bg-white/[0.05] transition-all group"
              >
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center mb-3 group-hover:bg-purple-500/20 transition">
                  <f.icon className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1.5">{f.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison Table */}
        <section className="max-w-3xl mx-auto px-6 pb-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">How We Compare</h2>
          <p className="text-white/40 text-sm text-center mb-10 max-w-lg mx-auto">DJsandMCMedia&trade; vs. traditional streaming platforms.</p>
          <div className="rounded-2xl border border-white/[0.08] overflow-hidden" data-testid="whyus-comparison-table">
            {/* Header */}
            <div className="grid grid-cols-3 bg-white/[0.05] border-b border-white/[0.08]">
              <div className="px-5 py-3 text-white/50 text-xs font-medium uppercase tracking-wider">Feature</div>
              <div className="px-5 py-3 text-center text-purple-400 text-xs font-semibold uppercase tracking-wider">DJsandMCMedia&trade;</div>
              <div className="px-5 py-3 text-center text-white/40 text-xs font-medium uppercase tracking-wider">Other Platforms</div>
            </div>
            {/* Rows */}
            {comparison.map((row, i) => (
              <div key={i} className={`grid grid-cols-3 border-b border-white/[0.05] ${i % 2 === 0 ? '' : 'bg-white/[0.02]'}`}>
                <div className="px-5 py-3 text-white/70 text-sm">{row.feature}</div>
                <div className="px-5 py-3 flex justify-center">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <div className="px-5 py-3 flex justify-center">
                  {row.them === true ? (
                    <Check className="w-5 h-5 text-green-400/50" />
                  ) : row.them === false ? (
                    <X className="w-5 h-5 text-red-400/50" />
                  ) : (
                    <span className="text-yellow-400/60 text-xs font-medium">{row.them}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Social Proof / Testimonials */}
        <section className="max-w-4xl mx-auto px-6 pb-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">What Creators Are Saying</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="whyus-testimonials">
            {[
              { quote: "Finally a platform that lets me own my brand. My listeners think it's a custom-built app.", name: "DJ Fresh", role: "Hip-Hop DJ" },
              { quote: "The playlist scheduler and AutoDJ keep my station running 24/7. I set it and forget it.", name: "MC Blaze", role: "Radio Host" },
              { quote: "Live video + chat + song requests? My audience engagement tripled in the first week.", name: "Vibe Radio", role: "Podcast Network" },
            ].map((t, i) => (
              <div key={i} data-testid={`whyus-testimonial-${i}`} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
                <p className="text-white/60 text-sm leading-relaxed mb-4 italic">"{t.quote}"</p>
                <div>
                  <p className="text-white font-semibold text-sm">{t.name}</p>
                  <p className="text-white/30 text-xs">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="max-w-3xl mx-auto px-6 pb-24">
          <div className="rounded-2xl p-10 text-center" style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", border: "1px solid rgba(139,92,246,0.15)" }}>
            <Crown className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Ready to Own Your Sound?</h2>
            <p className="text-white/50 text-sm mb-6 max-w-md mx-auto">
              Stop renting space on someone else's platform. Build your channel, grow your brand, keep your audience.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/#pricing" data-testid="whyus-bottom-cta"
                className="inline-flex items-center justify-center gap-2 text-white px-8 py-3 rounded-full font-semibold text-sm transition hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #8B5CF6, #ec4899)" }}>
                View Plans <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/features" data-testid="whyus-features-link"
                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-3 rounded-full font-semibold text-sm transition">
                Explore Features
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
